/**
 * Reusable sortable table header + hook for AlphaScout tables.
 * - Click a column to sort; click again to flip direction.
 * - Shows muted up-down icon when inactive, solid up/down when active.
 */
import React, { useMemo, useState, useCallback } from 'react';
import { ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';

/**
 * useSortable
 * @param {Array} data - rows to sort
 * @param {Object} options
 *   - initialField: default sort key
 *   - initialDir: 'asc' | 'desc' (default 'desc')
 *   - accessors: { [field]: (row) => value } optional custom getters
 */
export const useSortable = (data, { initialField = null, initialDir = 'desc', accessors = {} } = {}) => {
  const [field, setField] = useState(initialField);
  const [dir, setDir] = useState(initialDir);

  const toggle = useCallback((key) => {
    if (key === field) {
      setDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setField(key);
      setDir('desc');
    }
  }, [field]);

  const sorted = useMemo(() => {
    if (!field || !Array.isArray(data)) return data || [];
    const getter = accessors[field] || ((r) => r?.[field]);
    const copy = [...data];
    copy.sort((a, b) => {
      const va = getter(a);
      const vb = getter(b);
      // Nulls go last regardless of direction
      const aNull = va === null || va === undefined || va === '';
      const bNull = vb === null || vb === undefined || vb === '';
      if (aNull && bNull) return 0;
      if (aNull) return 1;
      if (bNull) return -1;
      let cmp;
      if (typeof va === 'number' && typeof vb === 'number') {
        cmp = va - vb;
      } else {
        cmp = String(va).localeCompare(String(vb), undefined, { numeric: true });
      }
      return dir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [data, field, dir, accessors]);

  return { sorted, field, dir, toggle };
};

/**
 * SortHeader — renders a clickable <th> with arrow indicator.
 * Props:
 *   - label: display text
 *   - sortKey: string identifier
 *   - active: currently active sort key
 *   - dir: 'asc'|'desc'
 *   - onToggle: fn(sortKey)
 *   - align: 'left'|'right' (default 'left')
 *   - className: extra classes
 */
export const SortHeader = ({
  label,
  sortKey,
  active,
  dir,
  onToggle,
  align = 'left',
  className = '',
  testId,
  tooltip,
}) => {
  const isActive = active === sortKey;
  const Icon = !isActive ? ArrowUpDown : dir === 'asc' ? ArrowUp : ArrowDown;
  const justify = align === 'right' ? 'justify-end' : 'justify-start';
  return (
    <th
      onClick={() => onToggle(sortKey)}
      className={`px-2 py-2 cursor-pointer select-none group hover:text-foreground transition-colors ${
        align === 'right' ? 'text-right' : 'text-left'
      } ${className}`}
      data-testid={testId || `sort-header-${sortKey}`}
      title={tooltip}
    >
      <span className={`inline-flex items-center gap-1 ${justify}`}>
        <span>{label}</span>
        <Icon
          className={`w-3 h-3 ${
            isActive ? 'text-accent' : 'text-muted-foreground/40 group-hover:text-muted-foreground'
          }`}
        />
      </span>
    </th>
  );
};

export default SortHeader;
